import { Check, ChevronsUpDown } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { isPlatformUser } from "@/features/auth/roles";
import { useAuthStore } from "@/features/auth/store/auth-store";
import {
  type BusinessPractitioner,
  listBusinessPractitioners,
} from "@/features/business-practitioners/api";
import { useFirmContextStore } from "@/features/firm-context/store/firm-context-store";
import { cn } from "@/lib/utils";

/** Two-letter initials for the firm badge. */
function initials(name: string) {
  return (
    name
      .split(" ")
      .map((part) => part[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?"
  );
}

interface FirmSwitcherProps {
  collapsed: boolean;
}

/**
 * The sidebar firm workspace control. A house Team Leader / staffer with at
 * least one assigned Business Practitioner can switch between the in-house (CAP)
 * book and an assigned firm; the selection drives the Clients, Practitioners,
 * and New-Arrivals pages via the firm-context store.
 *
 * Everyone else (BP members, unassigned house users, and GA/SA — who drill in
 * from the full BP directory instead) gets the plain, non-switching card.
 */
export function FirmSwitcher({ collapsed }: FirmSwitcherProps) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const activeFirmId = useFirmContextStore((s) => s.activeFirmId);
  const activeFirmName = useFirmContextStore((s) => s.activeFirmName);
  const setActiveFirm = useFirmContextStore((s) => s.setActiveFirm);
  const clearFirm = useFirmContextStore((s) => s.clearFirm);

  const isHouse = user?.firm?.org_type === "house";
  const isPlatform = isPlatformUser(user);
  const houseName = user?.firm?.name ?? "NSI Capitals";

  const [assigned, setAssigned] = useState<BusinessPractitioner[]>([]);
  useEffect(() => {
    // Only house, non-platform users have a bounded assigned portfolio to offer.
    // Depend on primitives so an unrelated user-object change doesn't refetch.
    if (!isHouse || isPlatform) return;
    let active = true;
    listBusinessPractitioners()
      .then((list) => active && setAssigned(list))
      .catch(() => active && setAssigned([]));
    return () => {
      active = false;
    };
  }, [isHouse, isPlatform]);

  const canSwitch = isHouse && !isPlatform && assigned.length > 0;

  // The name/sublabel shown on the card: the active BP when drilled in, else the
  // in-house book.
  const displayName = canSwitch && activeFirmId ? (activeFirmName ?? "Business Practitioner") : houseName;
  const sublabel = canSwitch && activeFirmId ? "Acting on behalf" : "Firm workspace";

  const badge = (
    <div
      className={cn(
        "bg-primary font-heading flex shrink-0 items-center justify-center rounded-[10px] font-bold text-white",
        collapsed ? "size-10 text-xs" : "size-8 text-xs",
      )}
    >
      {initials(displayName)}
    </div>
  );

  if (!canSwitch) {
    // Plain card — nothing to switch into.
    return collapsed ? (
      <div className="mx-auto" aria-label={displayName} title={displayName}>
        {badge}
      </div>
    ) : (
      <div className="mt-2 mb-1 flex items-center gap-2.5 rounded-md border border-white/8 bg-white/6 p-3.5">
        {badge}
        <div className="flex min-w-0 flex-col leading-tight">
          <span className="truncate text-[13px] font-semibold text-white">{displayName}</span>
          <span className="text-sidebar-muted text-[11px]">{sublabel}</span>
        </div>
      </div>
    );
  }

  function selectHouse() {
    clearFirm();
    navigate("/clients");
  }
  function selectFirm(bp: BusinessPractitioner) {
    setActiveFirm(bp.id, bp.name);
    navigate("/clients");
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Switch firm workspace"
        className={cn(
          "hover:bg-white/10 focus-visible:ring-ring flex items-center rounded-md border border-white/8 bg-white/6 outline-none transition-colors focus-visible:ring-2",
          collapsed ? "mx-auto p-1" : "mt-2 mb-1 w-full gap-2.5 p-3.5",
        )}
      >
        {badge}
        {!collapsed && (
          <>
            <div className="flex min-w-0 flex-col text-left leading-tight">
              <span className="truncate text-[13px] font-semibold text-white">{displayName}</span>
              <span className="text-sidebar-muted text-[11px]">{sublabel}</span>
            </div>
            <ChevronsUpDown className="text-sidebar-muted ml-auto size-4 shrink-0" />
          </>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="w-56">
        <DropdownMenuLabel>Switch workspace</DropdownMenuLabel>
        <DropdownMenuItem onSelect={selectHouse} className="gap-2">
          <Check className={cn("size-4", activeFirmId ? "opacity-0" : "opacity-100")} />
          <span className="truncate">In-house · {houseName}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {assigned.map((bp) => (
          <DropdownMenuItem key={bp.id} onSelect={() => selectFirm(bp)} className="gap-2">
            <Check
              className={cn("size-4", activeFirmId === bp.id ? "opacity-100" : "opacity-0")}
            />
            <span className="truncate">{bp.name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
